/**
 * Line-level helpers for the YAML parser: indentation, comments, the colon that makes a
 * line a mapping entry, and the anchor and tag that may precede a node.
 */

import { quotedEnd } from "@/lib/tool-kit/latex-dev/yaml-text";

export interface Line {
  text: string;
  /** 1-based number in the original text. */
  no: number;
}

export interface Props {
  anchor: string | null;
  tag: string | null;
  /** What follows the properties, with leading white space removed. */
  rest: string;
  /** Characters taken, including the white space before and after the properties. */
  used: number;
}

const RE_BLANK = /^[ \t]*$/;
const RE_ANCHOR = /^&[^\s,[\]{}]+/;
const RE_TAG = /^!(?:<[^>]*>|[^\s,[\]{}]*)/;
const RE_CORE_TAG =
  /^(?:!!|!<tag:yaml\.org,2002:)(str|int|float|bool|null|seq|map)>?$/;

export function indentOf(text: string): number {
  let n = 0;
  while (n < text.length && text[n] === " ") {
    n++;
  }
  return n;
}

export function isBlankLine(text: string): boolean {
  return RE_BLANK.test(text);
}

export function isCommentLine(text: string): boolean {
  return text.trimStart().startsWith("#");
}

function startsComment(text: string, i: number): boolean {
  return (
    text[i] === "#" && (i === 0 || text[i - 1] === " " || text[i - 1] === "\t")
  );
}

/** A line's text without a trailing comment (a `#` that follows white space). */
export function stripComment(text: string): string {
  for (let i = 0; i < text.length; i++) {
    if (startsComment(text, i)) {
      return text.slice(0, i).trimEnd();
    }
  }
  return text.trimEnd();
}

/** The core type a tag names (`!!str` → "str"), or null for any other tag. */
export function coreTag(tag: string | null): string | null {
  if (tag === null) {
    return null;
  }
  const match = RE_CORE_TAG.exec(tag);
  return match ? match[1] : null;
}

function quotedKeyColon(content: string): number {
  const end = quotedEnd(content, 0);
  if (end < 0) {
    return -1;
  }
  let j = end;
  while (content[j] === " " || content[j] === "\t") {
    j++;
  }
  const next = content[j + 1];
  const ok =
    content[j] === ":" && (next === undefined || next === " " || next === "\t");
  return ok ? j : -1;
}

/** Index of the colon that makes `content` a mapping entry (`key: value`), or -1. */
export function mappingColon(content: string): number {
  const first = content[0];
  if (first === '"' || first === "'") {
    return quotedKeyColon(content);
  }
  if (first === "[" || first === "{") {
    return -1;
  }
  for (let k = 0; k < content.length; k++) {
    if (startsComment(content, k)) {
      return -1;
    }
    const next = content[k + 1];
    if (
      content[k] === ":" &&
      (next === undefined || next === " " || next === "\t")
    ) {
      return k;
    }
  }
  return -1;
}

/** Read the anchors (`&a`) and tags (`!!str`) that start a node. */
export function parseProps(text: string): Props {
  let rest = text.trimStart();
  let anchor: string | null = null;
  let tag: string | null = null;
  let used = text.length - rest.length;
  for (;;) {
    const a = RE_ANCHOR.exec(rest);
    const t = RE_TAG.exec(rest);
    const hit = a ?? t;
    if (!hit) {
      break;
    }
    if (a) {
      anchor = a[0].slice(1);
    } else {
      tag = hit[0];
    }
    const trimmed = rest.slice(hit[0].length).trimStart();
    used += rest.length - trimmed.length;
    rest = trimmed;
  }
  return { anchor, tag, rest, used };
}
